// "Not called yesterday" — accountability list: follow-ups that were due
// (yesterday or earlier) with no call logged since. Actions: Called (done),
// Postpone to a date, or mark Lost.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PhoneMissed, CheckCircle2, CalendarClock, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtD = (iso) => {
  if (!iso) return 'never';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

const Row = ({ it, onAction, selected, onToggle }) => {
  const [busy, setBusy] = useState(false);
  const [showPostpone, setShowPostpone] = useState(false);
  const [date, setDate] = useState('');

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === 'called') {
        await axios.post(`${API}/follow-ups/${it.task_id}/complete`, { note: 'confirmed called (supervisor)' });
        toast.success('Marked as called');
      } else if (kind === 'lost') {
        const note = window.prompt('Optional note — why is this lead lost?') || '';
        await axios.post(`${API}/follow-ups/${it.task_id}/lost`, { note });
        toast.success('Marked as lost');
      } else if (kind === 'postpone') {
        if (!date) { toast.error('Pick a date first'); setBusy(false); return; }
        await axios.post(`${API}/follow-ups/${it.task_id}/postpone`, { due_date: date });
        toast.success(`Postponed to ${fmtD(date)}`);
      }
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-2.5 flex items-center gap-3 flex-wrap border-t border-slate-100 first:border-t-0" data-testid={`not-called-row-${it.task_id}`}>
      <input type="checkbox" checked={selected} onChange={() => onToggle(it.task_id)}
        className="w-4 h-4 accent-teal-600 cursor-pointer" data-testid={`not-called-select-${it.task_id}`} />
      <div className="min-w-[180px]">
        <p className="text-sm font-semibold text-slate-800 font-mono">{it.customer_phone}</p>
        <p className="text-[11px] text-slate-500">{it.agent_name} · ext {it.agent_id}</p>
      </div>
      <div className="text-xs">
        <span className="inline-flex items-center gap-1 font-bold text-red-600" data-testid={`not-called-overdue-${it.task_id}`}>
          <PhoneMissed className="w-3 h-3" /> due {fmtD(it.due_date)} · {it.days_overdue}d overdue
        </span>
        <span className="text-slate-400 ml-2">last called: {fmtD(it.last_called_at)}</span>
        {it.postponed_count > 0 && (
          <span className={`ml-2 font-bold ${it.postponed_count >= 3
            ? 'text-red-700 bg-red-100 border border-red-200 rounded px-1.5 py-0.5'
            : 'text-amber-600'}`}
            data-testid={`serial-snooze-${it.task_id}`}>
            {it.postponed_count >= 3 ? `serial snooze ×${it.postponed_count}` : `postponed ×${it.postponed_count}`}
          </span>
        )}
      </div>
      <div className="ml-auto flex items-center gap-1.5 flex-wrap">
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
          <>
            <button onClick={() => act('called')}
              className="text-[11px] font-semibold inline-flex items-center gap-1 border border-emerald-300 text-emerald-700 rounded px-2 py-1 hover:bg-emerald-50"
              data-testid={`not-called-done-${it.task_id}`}>
              <CheckCircle2 className="w-3 h-3" /> Called
            </button>
            {showPostpone ? (
              <span className="inline-flex items-center gap-1">
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5"
                  data-testid={`not-called-postpone-date-${it.task_id}`} />
                <button onClick={() => act('postpone')}
                  className="text-[11px] font-semibold border border-amber-300 text-amber-700 rounded px-2 py-1 hover:bg-amber-50"
                  data-testid={`not-called-postpone-confirm-${it.task_id}`}>Set</button>
              </span>
            ) : (
              <button onClick={() => setShowPostpone(true)}
                className="text-[11px] font-semibold inline-flex items-center gap-1 border border-amber-300 text-amber-700 rounded px-2 py-1 hover:bg-amber-50"
                data-testid={`not-called-postpone-${it.task_id}`}>
                <CalendarClock className="w-3 h-3" /> Postpone
              </button>
            )}
            <button onClick={() => act('lost')}
              className="text-[11px] font-semibold inline-flex items-center gap-1 border border-red-200 text-red-600 rounded px-2 py-1 hover:bg-red-50"
              data-testid={`not-called-lost-${it.task_id}`}>
              <XCircle className="w-3 h-3" /> Lost
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const NotCalledCard = ({ domainId = null }) => {
  const [data, setData] = useState(null);
  const [agent, setAgent] = useState('all');
  const [sel, setSel] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDate, setBulkDate] = useState('');

  const load = useCallback(() => {
    const p = domainId ? `?domain_id=${domainId}` : '';
    axios.get(`${API}/analytics/supervisor/not-called${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [domainId]);
  useEffect(() => { load(); }, [load]);

  if (!data || data.count === 0) return null;
  const agents = [...new Set(data.items.map((i) => i.agent_name).filter(Boolean))].sort();
  const items = data.items.filter((i) => agent === 'all' || i.agent_name === agent);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = items.length > 0 && items.every((i) => sel.includes(i.task_id));

  const bulk = async (kind) => {
    const ids = items.filter((i) => sel.includes(i.task_id)).map((i) => i.task_id);
    if (!ids.length) { toast.error('Select at least one row'); return; }
    if (kind === 'postpone' && !bulkDate) { toast.error('Pick a date first'); return; }
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const id of ids) {
      try {
        if (kind === 'called') await axios.post(`${API}/follow-ups/${id}/complete`, { note: 'bulk: confirmed called (supervisor)' });
        else if (kind === 'lost') await axios.post(`${API}/follow-ups/${id}/lost`, { note: 'bulk marked lost (supervisor)' });
        else await axios.post(`${API}/follow-ups/${id}/postpone`, { due_date: bulkDate });
        ok += 1;
      } catch { fail += 1; }
    }
    toast[fail ? 'warning' : 'success'](`${ok} updated${fail ? `, ${fail} failed` : ''}`);
    setSel([]); setBulkBusy(false); load();
  };

  return (
    <section data-testid="not-called-section">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-sm font-bold uppercase tracking-wide text-red-700 flex items-center gap-1.5">
          <PhoneMissed className="w-4 h-4" /> Not called — was due
        </h2>
        <span className="text-[11px] font-bold bg-red-100 text-red-700 rounded-full px-2 py-0.5" data-testid="not-called-count">{data.count}</span>
        <div className="flex gap-1 flex-wrap ml-2" data-testid="not-called-agent-filter">
          <button onClick={() => setAgent('all')}
            className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${agent === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            data-testid="not-called-agent-all">All</button>
          {agents.map((a) => (
            <button key={a} onClick={() => setAgent(a)}
              className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${agent === a ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              data-testid={`not-called-agent-${a.replace(/\s/g, '-')}`}>{a}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="not-called-bulk-bar">
        <label className="text-[11px] font-semibold text-slate-500 inline-flex items-center gap-1.5">
          <input type="checkbox" checked={allSel} className="w-4 h-4 accent-teal-600"
            onChange={() => setSel(allSel ? [] : items.map((i) => i.task_id))} data-testid="not-called-select-all" />
          Select all ({items.length})
        </label>
        {sel.length > 0 && (
          <>
            <span className="text-[11px] font-bold text-teal-700">{sel.length} selected</span>
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
              <>
                <button onClick={() => bulk('called')} className="text-[11px] font-semibold border border-emerald-300 text-emerald-700 rounded px-2 py-1 hover:bg-emerald-50" data-testid="not-called-bulk-called">Mark called</button>
                <input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5" data-testid="not-called-bulk-date" />
                <button onClick={() => bulk('postpone')} className="text-[11px] font-semibold border border-amber-300 text-amber-700 rounded px-2 py-1 hover:bg-amber-50" data-testid="not-called-bulk-postpone">Postpone</button>
                <button onClick={() => bulk('lost')} className="text-[11px] font-semibold border border-red-200 text-red-600 rounded px-2 py-1 hover:bg-red-50" data-testid="not-called-bulk-lost">Mark lost</button>
              </>
            )}
          </>
        )}
      </div>
      <div className="rounded-xl border border-red-200 bg-white overflow-hidden">
        {items.map((it) => <Row key={it.task_id} it={it} onAction={load} selected={sel.includes(it.task_id)} onToggle={toggle} />)}
      </div>
    </section>
  );
};

export default NotCalledCard;
